import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "react-query";
import NotificationDropdown from "./NotificationDropdown";
import NotificationProvider from "./NotificationProvider";
import { useUnreadCount } from "../../hooks/notifications/useNotifications";
import { useSharedEventBridgeNotifications } from "../../hooks/notifications/useSharedEventBridgeNotifications";

/**
 * Inner NotificationBell Component that uses React Query
 */
const NotificationBellContent = ({
  className = "",
  position = "right",
  showDropdown = true,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);

  // Use React Query for API unread count
  const { data: unreadCountData, isLoading } = useUnreadCount();
  const apiUnreadCount = unreadCountData?.data?.unreadCount || 0;

  // Use EventBridge notifications for real-time unread count
  const { notifications: eventBridgeNotifications } =
    useSharedEventBridgeNotifications();

  const queryClient = useQueryClient();

  // Invalidate React Query cache when new WebSocket notifications arrive
  useEffect(() => {
    if (eventBridgeNotifications.length > 0) {
      // Invalidate cache to ensure API queries are updated
      queryClient.invalidateQueries("notifications");
      queryClient.invalidateQueries("unreadCount");
    }
  }, [eventBridgeNotifications.length, queryClient]);

  // Use API unread count as primary source (it's authoritative)
  // EventBridge notifications are for real-time updates but API is the source of truth
  const unreadCount = apiUnreadCount;

  // Handle click outside to close dropdown
  const handleClickOutside = useCallback(
    (event) => {
      if (
        isDropdownOpen &&
        bellRef.current &&
        dropdownRef.current &&
        !bellRef.current.contains(event.target) &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    },
    [isDropdownOpen]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Handle bell click
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const newState = !isDropdownOpen;
    setIsDropdownOpen(newState);

    // React Query will handle fetching in the dropdown component
  };

  return (
    <div className={`notification-bell-container relative ${className}`}>
      <button
        ref={bellRef}
        onClick={handleClick}
        className="relative p-2 rounded-full hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gold transition-all duration-200 min-h-[36px] min-w-[36px] flex items-center justify-center"
        style={{
          zIndex: 9999,
          pointerEvents: "auto",
          cursor: "pointer",
        }}
        disabled={isLoading}
        aria-label={`Notifications${
          unreadCount > 0 ? ` (${unreadCount} unread)` : ""
        }`}
      >
        <FontAwesomeIcon
          icon={faBell}
          className={`text-white hover:text-gold transition-colors ${
            isLoading ? "animate-pulse" : ""
          }`}
        />

        {/* Simple notification indicator */}
        {unreadCount > 0 && (
          <span
            className="absolute top-0 right-0 w-3 h-3 bg-deep-red rounded-full animate-pulse border-2 border-dark-grey"
            role="status"
            aria-label={`${unreadCount} unread notifications`}
          />
        )}
      </button>

      {/* Notification Dropdown */}
      {showDropdown && isDropdownOpen && (
        <div ref={dropdownRef}>
          <NotificationDropdown
            isOpen={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            position={position}
          />
        </div>
      )}
    </div>
  );
};

/**
 * NotificationBell Component
 *
 * Clean notification bell with dropdown functionality
 * Uses the existing QueryClient to ensure cache consistency
 */
const NotificationBell = (props) => {
  const queryClient = useQueryClient();

  return (
    <NotificationProvider queryClient={queryClient}>
      <NotificationBellContent {...props} />
    </NotificationProvider>
  );
};

export default NotificationBell;
